import { useState, useEffect, useRef, useMemo } from 'react'
import {
    AlertTriangle,
    Clock,
    AlertCircle,
    ExternalLink,
    ChevronDown,
    ChevronUp,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getTicketSLASummary,
    getTicketsAtRisk,
    getTicketsApproaching,
} from '@/modules/Service/services/sla'
import { TicketAtRisk, TicketApproaching } from '@/modules/Service/types/sla'
import { generatePathDatacentral, paths } from '@/utils/path'
import { formatMinutes } from '../../Shared/utils/format-value'
import SlaProgressBar from '../components/sla-progress-bar'

const useAtRiskColumns = (): DataTableColumn<TicketAtRisk>[] =>
    useMemo(
        () => [
            {
                key: 'ticket_id',
                title: 'Ticket',
                width: 80,
                render: (_val, row) => (
                    <span className="font-medium">#{row.ticket_id}</span>
                ),
            },
            {
                key: 'customer_name',
                title: 'Khách hàng',
                render: (_val, row) => (
                    <span className="text-sm">{row.customer_name}</span>
                ),
            },
            {
                key: 'elapsed_minutes',
                title: 'Đã trôi qua',
                headerClassName: 'text-center',
                cellClassName: 'text-center font-medium text-red-600',
                render: (_val, row) => formatMinutes(row.elapsed_minutes),
            },
            {
                key: 'sla_threshold',
                title: 'SLA',
                headerClassName: 'text-center',
                cellClassName: 'text-center text-gray-500 text-sm',
                render: (_val, row) => formatMinutes(row.sla_threshold),
            },
            {
                key: 'breach_percent' as keyof TicketAtRisk,
                title: 'Vi phạm',
                width: 144,
                render: (_val, row) => (
                    <SlaProgressBar
                        percent={row.breach_percent}
                        isBreached={true}
                    />
                ),
            },
            {
                key: 'actions',
                title: '',
                width: 40,
                render: (_val, row) => (
                    <Link
                        to={generatePathDatacentral(paths.ticketDetail, {
                            id: row.ticket_id,
                        })}
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                        >
                            <ExternalLink className="w-3 h-3" />
                        </Button>
                    </Link>
                ),
            },
        ],
        []
    )

const useApproachingColumns = (): DataTableColumn<TicketApproaching>[] =>
    useMemo(
        () => [
            {
                key: 'ticket_id',
                title: 'Ticket',
                width: 80,
                render: (_val, row) => (
                    <span className="font-medium">#{row.ticket_id}</span>
                ),
            },
            {
                key: 'customer_name',
                title: 'Khách hàng',
                render: (_val, row) => (
                    <span className="text-sm">{row.customer_name}</span>
                ),
            },
            {
                key: 'elapsed_minutes',
                title: 'Đã trôi qua',
                headerClassName: 'text-center',
                cellClassName: 'text-center font-medium text-orange-600',
                render: (_val, row) => formatMinutes(row.elapsed_minutes),
            },
            {
                key: 'sla_threshold',
                title: 'SLA',
                headerClassName: 'text-center',
                cellClassName: 'text-center text-gray-500 text-sm',
                render: (_val, row) => formatMinutes(row.sla_threshold),
            },
            {
                key: 'progress_percent' as keyof TicketApproaching,
                title: 'Tiến độ',
                width: 144,
                render: (_val, row) => (
                    <SlaProgressBar
                        percent={row.progress_percent}
                        isBreached={false}
                    />
                ),
            },
            {
                key: 'actions',
                title: '',
                width: 40,
                render: (_val, row) => (
                    <Link
                        to={generatePathDatacentral(paths.ticketDetail, {
                            id: row.ticket_id,
                        })}
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                        >
                            <ExternalLink className="w-3 h-3" />
                        </Button>
                    </Link>
                ),
            },
        ],
        []
    )

const TicketsSectionInner = () => {
    const [isAtRiskOpen, setIsAtRiskOpen] = useState(true)
    const [isApproachingOpen, setIsApproachingOpen] = useState(true)

    const { data: summaryData, isLoading: isLoadingSummary } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_TICKETS_SUMMARY],
            queryFn: getTicketSLASummary,
            refetchInterval: 60000,
        })
    const { data: atRiskData, isLoading: isLoadingAtRisk } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_TICKETS_AT_RISK],
            queryFn: () => getTicketsAtRisk({ limit: 20, offset: 0 }),
            refetchInterval: 60000,
        })
    const { data: approachingData, isLoading: isLoadingApproaching } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_TICKETS_APPROACHING],
            queryFn: () => getTicketsApproaching({ limit: 20, offset: 0 }),
            refetchInterval: 60000,
        })

    const summary = summaryData?.result
    const atRiskTickets: TicketAtRisk[] = atRiskData?.results || []
    const approachingTickets: TicketApproaching[] =
        approachingData?.results || []

    const atRiskColumns = useAtRiskColumns()
    const approachingColumns = useApproachingColumns()

    if (isLoadingSummary || isLoadingAtRisk || isLoadingApproaching) {
        return (
            <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-20" />
                    ))}
                </div>
                <Skeleton className="h-48" />
            </div>
        )
    }

    if (!summary || summary.total_warning === 0) {
        return (
            <Paper className="border border-gray-100 text-center">
                <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                    Không có ticket nào cần chú ý
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                    Tất cả ticket đang được xử lý đúng SLA
                </p>
            </Paper>
        )
    }

    return (
        <div className="space-y-2">
            {atRiskTickets.length > 0 && (
                <Paper className="border border-gray-100">
                    <div className="py-3 px-4">
                        <div
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => setIsAtRiskOpen((v) => !v)}
                        >
                            <div className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-red-500" />
                                <p className="text-sm font-medium">
                                    Ticket Vi Phạm SLA
                                </p>
                                <Badge
                                    variant="destructive"
                                    className="text-xs"
                                >
                                    {atRiskTickets.length}
                                </Badge>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                            >
                                {isAtRiskOpen ? (
                                    <ChevronUp className="w-4 h-4" />
                                ) : (
                                    <ChevronDown className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Ticket đã vượt quá thời hạn SLA (100%+)
                        </p>
                    </div>
                    {isAtRiskOpen && (
                        <DataTableCustomized<TicketAtRisk>
                            columns={atRiskColumns}
                            rows={atRiskTickets}
                            rowKey={(row) => row.ticket_id}
                            rowClassName="bg-red-50/50"
                            size="small"
                        />
                    )}
                </Paper>
            )}

            {approachingTickets.length > 0 && (
                <Paper className="border border-gray-100">
                    <div className="py-3 px-4">
                        <div
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => setIsApproachingOpen((v) => !v)}
                        >
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-orange-500" />
                                <p className="text-sm font-medium">
                                    Ticket Sắp Hết Hạn
                                </p>
                                <Badge className="bg-orange-100 text-orange-700 text-xs">
                                    {approachingTickets.length}
                                </Badge>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                            >
                                {isApproachingOpen ? (
                                    <ChevronUp className="w-4 h-4" />
                                ) : (
                                    <ChevronDown className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Ticket đang gần deadline (80–100%)
                        </p>
                    </div>
                    {isApproachingOpen && (
                        <DataTableCustomized<TicketApproaching>
                            columns={approachingColumns}
                            rows={approachingTickets}
                            rowKey={(row) => row.ticket_id}
                            rowClassName="bg-orange-50/50"
                            size="small"
                        />
                    )}
                </Paper>
            )}
        </div>
    )
}

/** Lazy-mount via IntersectionObserver */
const TicketsSection = ({ periodLabel }: { periodLabel: string }) => {
    const [mounted, setMounted] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    setMounted(true)
                    observer.disconnect()
                }
            },
            { rootMargin: '200px' }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <div>
            <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-700">
                    Ticket cần xử lý · {periodLabel}
                </h2>
            </div>
            <div ref={ref}>
                {mounted ? (
                    <TicketsSectionInner />
                ) : (
                    <Skeleton className="h-40 w-full" />
                )}
            </div>
        </div>
    )
}

export default TicketsSection
